import {
  ArrowsAltOutlined,
  CalendarOutlined,
  CloseOutlined,
  CopyOutlined,
  SettingOutlined,
  ShrinkOutlined,
} from '@ant-design/icons';
import {
  MAX_DATE_PLACEMENTS_PER_SIGNATURE,
  MAX_INPUT_PLACEMENTS_PER_SIGNATURE,
} from '@bmo/form-core';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Alert, Tooltip } from 'antd';
import type { PageViewport } from 'pdfjs-dist';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { SignaturePlacement } from '@/api/workflows';

import { PdfPageStack, usePdfDocumentPages, viewportFor } from './pdfPageRender';
import {
  movePlacement,
  placementToCssBox,
  PLACEMENT_PREVIEW_BUFFER_PX,
} from './pdfPlacementGeometry';

/**
 * One box on the page: a signature, or one of the companions paired with it — a
 * date label or an input box.
 *
 * `key` is what identifies it back to the page's state — the anchor for a
 * signature, and the anchor plus a suffix for a companion. Anchors are unique
 * within a document by construction, so this needs nothing more.
 */
export type PlacedBox = {
  key: string;
  anchor: string;
  /**
   * What the box collects: a signature, the date label paired with one, or a text
   * box the signer types into. The latter two are companions of the signature
   * named by `anchor` — both are added from its control row, both anchor off it
   * and both go when it does — so they share every field below except the ones
   * that number them within their own list.
   */
  kind: 'signature' | 'date' | 'input';
  placement: SignaturePlacement;
  /** Display name of the signatory who signs here. */
  signerLabel: string;
  /**
   * Which of this signatory's signatures in this document the box belongs to,
   * 1-based — the only thing separating two identically-named boxes on the same
   * page. Shown 1-based but derived from the 0-based anchor counter, so the
   * badge's "· 2" and the PDF's `[SignatorySig1]` are the same signature.
   */
  signerIndex: number;
  /** Stable per-signatory colour, so several signers stay distinguishable. */
  color: string;
  /** True when this box belongs to the signatory currently selected. */
  active: boolean;
  /** Whether a date box already exists for this signature (signature boxes only). */
  hasDate: boolean;
  /**
   * Whether this box collects initials rather than a full signature (signature
   * boxes only). Drives the badge's wording, the box's footprint and which way
   * the expand/shrink toggle points.
   */
  isInitials: boolean;
  /**
   * Whether this signature is gated by conditions. Tints the gear and shows a
   * marker on the box — the marker regardless of `disabled`, so an approver
   * reviewing a read-only workflow can still see that the signature is
   * conditional.
   */
  hasConditions: boolean;
  /**
   * The format tokens this date will print in, drawn inside the box (date boxes
   * only). The box is sized to exactly this text at `dateFontSize`, so what is
   * on screen is the footprint the date will occupy.
   */
  dateText?: string;
  /** Point size the placeholder is drawn at (date boxes only). */
  dateFontSize?: number;
  /**
   * Which of the signature's dates this is, 0-based (date boxes only) — the
   * position in that language's list, which is also what decides the box's
   * `label<i>` anchor. Identifies the box for duplicate and delete.
   */
  dateIndex?: number;
  /**
   * How many dates the signature carries in this language (date boxes only).
   * Numbers the badge once there is more than one, and withholds the duplicate
   * button at the cap.
   */
  dateCount?: number;
  /**
   * Which of the signature's input boxes this is, 0-based (input boxes only) —
   * the position in that language's list, which is what decides its `Input<n>`
   * anchor. Identifies the box for duplicate and delete, exactly as `dateIndex`
   * does for a date.
   */
  inputIndex?: number;
  /**
   * How many input boxes the signature carries in this language (input boxes
   * only). Numbers the badge past the first and withholds the duplicate button at
   * the cap.
   */
  inputCount?: number;
  /**
   * Whether the signature already carries the maximum number of input boxes
   * (signature boxes only) — withholds the add-input button rather than offering
   * one that does nothing.
   */
  atInputCap?: boolean;
};

type Props = {
  templateId: string;
  /** S3 object key of the PDF variant being placed against (EN or FR). */
  fileKey: string;
  boxes: PlacedBox[];
  zoom: number;
  /** Read-only workflow, or no signatory selected → the page is inert. */
  disabled: boolean;
  /** Click on empty page space with a signatory selected — place a new signature. */
  onPlace: (pageIndex: number, point: { x: number; y: number }, viewport: PageViewport) => void;
  onMove: (key: string, next: SignaturePlacement) => void;
  onDelete: (key: string) => void;
  onAddDate: (anchor: string) => void;
  /**
   * Add another date to a signature that already has one, copied from the date
   * at `index` — the button beside that box. One language only: the box lands
   * in the PDF currently on screen, since the other language's layout is a
   * different document entirely.
   */
  onDuplicateDate: (anchor: string, index: number) => void;
  /** Flip a signature placement between a full signature and initials. */
  onToggleInitials: (anchor: string) => void;
  /**
   * Add a text box the signer types into, as a companion of this signature — the
   * `>_` button on the signature's control row. One language only, like every
   * other box on this page.
   */
  onAddInput: (anchor: string) => void;
  /**
   * Add another input box to a signature that already has one, copied from the box
   * at `index` — the button beside that box, mirroring `onDuplicateDate`.
   */
  onDuplicateInput: (anchor: string, index: number) => void;
  /**
   * Open the show-conditions editor for a signature. Null hides the gear
   * entirely — the workflow has no field the signature could be gated on (see
   * `hasEligibleSourceFields`), so the modal would open on an empty picker.
   */
  onEditConditions: ((anchor: string) => void) | null;
};

/**
 * The e-signature placement editor's PDF surface.
 *
 * Renders the document exactly as the field-mapping viewer does (shared
 * `PdfPageStack`) but overlays draggable signature boxes instead of AcroForm
 * widgets. Clicking empty page space drops a new box; dragging moves it.
 *
 * Each box is the area the signature will actually fill: the anchor is stamped
 * at its bottom-left corner and OneSpan grows the widget up and to the right
 * from there, so what is drawn here is what gets signed. The outline sits
 * `PLACEMENT_PREVIEW_BUFFER_PX` outside that area on every side — clearance the
 * originator can see, nothing the stored placement knows about. There is no
 * resize handle — one consistent size across a form is what an originator wants — so a
 * box is one of exactly two footprints, `SIGNATURE_BOX` or the narrower
 * `INITIALS_BOX`, chosen by the expand/shrink toggle on the box itself.
 *
 * Date boxes are sized differently: they show the format tokens they will print
 * in, and their footprint is that text measured at the default point size
 * (`dateFormatMetrics.ts`). There is no arbitrary rectangle to choose and no
 * per-signature customization — format/font size are fixed to the default for
 * every date box (BMO's v8 save-application contract no longer accepts
 * `formatString`/`fontSize`, so there is nothing left to configure).
 */
export function PdfSignaturePlacementViewer({
  templateId,
  fileKey,
  boxes,
  zoom,
  disabled,
  onPlace,
  onMove,
  onDelete,
  onAddDate,
  onDuplicateDate,
  onToggleInitials,
  onAddInput,
  onDuplicateInput,
  onEditConditions,
}: Props) {
  const { t } = useTranslation();
  const { pages, pageProxiesRef, loading, error } = usePdfDocumentPages(templateId, fileKey);

  // A few pixels of travel before a drag starts, so clicking a box's delete
  // button is not swallowed as a one-pixel drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const box = boxes.find((b) => b.key === event.active.id);
    if (!box) return;
    const page = pageProxiesRef.current[box.placement.page];
    if (!page) return;
    // The SAME viewport the canvas was rasterized with, so the pixel delta
    // converts back to points against the geometry actually on screen.
    onMove(box.key, movePlacement(box.placement, event.delta, viewportFor(page, zoom)));
  };

  if (error) {
    return (
      <div style={{ padding: 24, color: '#b91c1c', fontSize: 13 }}>
        {t('pdfMapping.loadPdfFailed')}
      </div>
    );
  }
  if (loading) {
    return (
      <div style={{ padding: 24, color: '#6b7280', fontSize: 13 }}>
        {t('pdfMapping.loadingPdf')}
      </div>
    );
  }

  // Boxes parked past the end of this PDF. They render NOWHERE — the overlay below draws
  // per existing page — so without this they are invisible to the one person who can fix
  // them, while still reaching BMO: `BmoDataBuilder` counts a signature's date and input
  // placements without ever reading their geometry (it has no PDF to check against, by
  // design — ADR-0030 keeps geometry platform-side), so it describes the field, and
  // `stampAnchors` then skips the white text with nothing but a console warning. The
  // package ships naming an anchor the document does not contain, and nothing surfaces it
  // until somebody tries to sign.
  //
  // This is the only layer that can make the check: the page count lives in the loaded
  // document, which the domain never sees and which changes under a re-upload — see
  // `WorkflowStep.ValidatePlacement`, which declines the check for exactly that reason.
  // Deliberately NOT "same page as its signature": a date may legitimately sit on another
  // page (spec §5.4:214 — "beside the signature line and again in a summary block"), since
  // the wire carries the anchor string and no geometry, and OneSpan finds that literal
  // anywhere in the document.
  const orphanedBoxes = boxes.filter((b) => b.placement.page >= pages.length);

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      {orphanedBoxes.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('signaturePlacement.offPageBoxes.title')}
          description={
            <>
              <div style={{ marginBottom: 6 }}>
                {t('signaturePlacement.offPageBoxes.body', { pageCount: pages.length })}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {orphanedBoxes.map((b) => (
                  <li key={b.key}>
                    {t(`signaturePlacement.offPageBoxes.kind.${b.kind}`)} — {b.signerLabel} ·{' '}
                    {b.signerIndex} ({b.anchor}) — {t('signaturePlacement.offPageBoxes.onPage')}{' '}
                    {b.placement.page + 1}
                  </li>
                ))}
              </ul>
            </>
          }
        />
      )}
      <PdfPageStack
        pages={pages}
        zoom={zoom}
        pageProxiesRef={pageProxiesRef}
        renderOverlay={(pageIndex, viewport) => (
          <>
            {/* Full-page click target, BEHIND the boxes, so clicking an existing
                box selects/drags it rather than dropping another one on top. */}
            <div
              role="presentation"
              onClick={(e: ReactMouseEvent<HTMLDivElement>) => {
                if (disabled) return;
                const rect = e.currentTarget.getBoundingClientRect();
                onPlace(pageIndex, { x: e.clientX - rect.left, y: e.clientY - rect.top }, viewport);
              }}
              style={{
                position: 'absolute',
                inset: 0,
                cursor: disabled ? 'default' : 'copy',
              }}
            />
            {boxes
              .filter((b) => b.placement.page === pageIndex)
              .map((box) => (
                <DraggableBox
                  key={box.key}
                  box={box}
                  viewport={viewport}
                  disabled={disabled}
                  onDelete={() => onDelete(box.key)}
                  onAddDate={() => onAddDate(box.anchor)}
                  onDuplicateDate={() => onDuplicateDate(box.anchor, box.dateIndex ?? 0)}
                  onToggleInitials={() => onToggleInitials(box.anchor)}
                  onAddInput={() => onAddInput(box.anchor)}
                  onDuplicateInput={() => onDuplicateInput(box.anchor, box.inputIndex ?? 0)}
                  onEditConditions={onEditConditions ? () => onEditConditions(box.anchor) : null}
                />
              ))}
          </>
        )}
      />
    </DndContext>
  );
}

function DraggableBox({
  box,
  viewport,
  disabled,
  onDelete,
  onAddDate,
  onDuplicateDate,
  onToggleInitials,
  onAddInput,
  onDuplicateInput,
  onEditConditions,
}: {
  box: PlacedBox;
  viewport: PageViewport;
  disabled: boolean;
  onDelete: () => void;
  onAddDate: () => void;
  onDuplicateDate: () => void;
  onToggleInitials: () => void;
  onAddInput: () => void;
  onDuplicateInput: () => void;
  onEditConditions: (() => void) | null;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: box.key,
    disabled,
  });

  const css = placementToCssBox(box.placement, viewport);
  const isDate = box.kind === 'date';
  const isInput = box.kind === 'input';
  // Named rather than spelled `!isDate && !isInput` at each of its uses: what the
  // controls below turn on is "this box is the signature", and a signature now has
  // two kinds of companion rather than one.
  const isSignature = box.kind === 'signature';

  // The positioned element is the AUTHORED rectangle, to the pixel: every child
  // below is placed against it (the date text on its bottom-left corner above
  // all), and the drag delta converts back to points from it. The visible
  // outline is a separate layer so the buffer can never leak into that geometry.
  const style: CSSProperties = {
    position: 'absolute',
    left: css.left,
    top: css.top,
    width: css.width,
    height: css.height,
    // dnd-kit reports the live drag offset here; the committed position is
    // written back in PDF points on drag end.
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    cursor: disabled ? 'not-allowed' : 'grab',
    opacity: isDragging ? 0.75 : 1,
    // Above the click-to-place layer, so a click on a box never also places one.
    zIndex: isDragging ? 3 : 2,
    boxSizing: 'border-box',
  };

  // Drawn `PLACEMENT_PREVIEW_BUFFER_PX` outside the footprint on every side, so
  // what the originator sees is the signature's area plus a little clearance
  // rather than a rectangle that reads as safe right up to its own border.
  //
  // It keeps its pointer events (overflowing a parent does not cost a child its
  // hit area) and its events bubble to the draggable above, so grabbing the box
  // by the border the originator can actually see drags it — rather than falling
  // through to the click-to-place layer and dropping a second signature on top of
  // the first.
  const frameStyle: CSSProperties = {
    position: 'absolute',
    inset: -PLACEMENT_PREVIEW_BUFFER_PX,
    border: `${box.active ? 2 : 1.5}px ${isDate ? 'dashed' : 'solid'} ${box.color}`,
    background: box.active ? `${box.color}33` : `${box.color}1f`,
    // Square corners are what tells an input box apart from the signature it
    // hangs off at a glance: the two share a colour and a footprint, and the badge
    // only reads once you look straight at it.
    borderRadius: isInput ? 0 : 3,
    boxSizing: 'border-box',
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <div aria-hidden data-testid="placement-frame" style={frameStyle} />

      {isDate && box.dateText && (
        // The date's placeholder: the format tokens themselves, black, at the
        // authored point size scaled to the current zoom. The box is measured
        // from exactly this string (`dateFormatMetrics.ts`), so the text fills it
        // — pinned to the bottom-left because that is where the anchor is
        // stamped and where OneSpan grows the field from.
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontSize: (box.dateFontSize ?? 0) * viewport.scale,
            lineHeight: 1,
            color: '#000',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {box.dateText}
        </span>
      )}

      {isInput && (
        // The placeholder names what the region is for. Unlike a date box —
        // whose text IS its footprint, measured from the tokens — this is a
        // label inside a box the originator sized, so it is centred vertically
        // the way a form field's own placeholder sits, and is muted rather than
        // black: nothing of it prints, and it should not read as content.
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: 4,
            right: 20,
            fontSize: Math.max(8, 9 * viewport.scale),
            lineHeight: 1.2,
            color: '#6b7280',
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            pointerEvents: 'none',
          }}
        >
          {t('signaturePlacement.inputPlaceholder')}
        </span>
      )}

      <span
        style={{
          position: 'absolute',
          // Lifted clear of the buffer outline rather than of the footprint, so
          // the badge keeps the same small gap above the border it labels.
          top: -16 - PLACEMENT_PREVIEW_BUFFER_PX,
          left: 0,
          fontSize: 9,
          lineHeight: '14px',
          padding: '0 3px',
          borderRadius: 2,
          whiteSpace: 'nowrap',
          color: '#fff',
          background: box.color,
          pointerEvents: 'none',
          maxWidth: 260,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {t(
          isDate
            ? // Numbered only once the signature carries more than one date —
              // a lone date needs no ordinal, and adding one everywhere would
              // churn the badge every existing box reads.
              (box.dateCount ?? 1) > 1
              ? 'signaturePlacement.dateBadgeNumbered'
              : 'signaturePlacement.dateBadge'
            : isInput
              ? // Numbered on the same rule as a date's, for the same reason.
                (box.inputCount ?? 1) > 1
                ? 'signaturePlacement.inputBadgeNumbered'
                : 'signaturePlacement.inputBadge'
              : box.isInitials
                ? 'signaturePlacement.initialsBadge'
                : 'signaturePlacement.signatureBadge',
          {
            signer: box.signerLabel,
            index: box.signerIndex,
            dateIndex: (box.dateIndex ?? 0) + 1,
            inputIndex: (box.inputIndex ?? 0) + 1,
          },
        )}
        {/* Rendered regardless of `disabled` — the control row (and with it the
            gear's blue tint) is hidden on a read-only workflow, so without this
            an approver has nothing on screen telling them the signature is
            conditional. */}
        {isSignature && box.hasConditions && (
          <span aria-label={t('signaturePlacement.conditionsSet')} style={{ marginLeft: 4 }}>
            ⚙
          </span>
        )}
      </span>

      {!disabled && (
        <div
          style={
            isDate
              ? // A date box is only as big as its text — roughly 55×11 points at
                // the default size — so its 16px controls cannot sit inside it.
                // They go to its right rather than above it: the row above is
                // where the badge sits, and the badge is far wider than the box.
                {
                  position: 'absolute',
                  left: '100%',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  // Clears the buffer outline as well as the box, so the controls
                  // never sit on top of the border they belong to.
                  marginLeft: 4 + PLACEMENT_PREVIEW_BUFFER_PX,
                  display: 'flex',
                  gap: 2,
                }
              : { position: 'absolute', top: 2, right: 2, display: 'flex', gap: 2 }
          }
          // Controls sit inside the draggable, so their pointer events must not
          // also start a drag.
          onPointerDown={(e) => e.stopPropagation()}
        >
          {isSignature && (
            <Tooltip
              title={t(
                box.isInitials
                  ? 'signaturePlacement.makeSignature'
                  : 'signaturePlacement.makeInitials',
              )}
            >
              <button
                type="button"
                aria-label={t(
                  box.isInitials
                    ? 'signaturePlacement.makeSignature'
                    : 'signaturePlacement.makeInitials',
                )}
                onClick={onToggleInitials}
                style={
                  box.isInitials ? { ...boxControlStyle, color: INITIALS_COLOR } : boxControlStyle
                }
              >
                {/* The icon shows the ACTION, not the state: a full signature
                    offers "shrink to initials", an initials box offers "expand
                    back to a signature". The badge says which one it currently
                    is, so the icon does not have to. */}
                {box.isInitials ? (
                  <ArrowsAltOutlined style={{ fontSize: 10 }} />
                ) : (
                  <ShrinkOutlined style={{ fontSize: 10 }} />
                )}
              </button>
            </Tooltip>
          )}
          {isSignature && onEditConditions && (
            <Tooltip
              title={t(
                box.hasConditions
                  ? 'signaturePlacement.conditionsSet'
                  : 'signaturePlacement.conditions',
              )}
            >
              <button
                type="button"
                aria-label={t('signaturePlacement.conditions')}
                onClick={onEditConditions}
                style={
                  box.hasConditions
                    ? { ...boxControlStyle, color: CONDITION_COLOR }
                    : boxControlStyle
                }
              >
                <SettingOutlined style={{ fontSize: 10 }} />
              </button>
            </Tooltip>
          )}
          {/* Both companions are added from the signature's own row — a date and
              an input hang off the signature, so there is nowhere else they could
              come from. Withheld rather than disabled once the signature has all
              it can take, the way the duplicate buttons below are. */}
          {isSignature && !box.hasDate && (
            <Tooltip title={t('signaturePlacement.addDate')}>
              <button
                type="button"
                aria-label={t('signaturePlacement.addDate')}
                onClick={onAddDate}
                style={boxControlStyle}
              >
                <CalendarOutlined style={{ fontSize: 10 }} />
              </button>
            </Tooltip>
          )}
          {isSignature && !box.atInputCap && (
            <Tooltip title={t('signaturePlacement.addInput')}>
              <button
                type="button"
                aria-label={t('signaturePlacement.addInput')}
                onClick={onAddInput}
                // Drawn as literal text rather than an icon: `>_` reads as "a
                // place to type" at a glance, and antd has no glyph for it —
                // `CodeOutlined` is `</>`, which reads as markup instead.
                style={{ ...boxControlStyle, ...inputGlyphStyle }}
              >
                &gt;_
              </button>
            </Tooltip>
          )}
          {/* Duplicate sits immediately left of the delete X, so the two
              destructive-adjacent controls stay where the originator already
              expects them. Withheld at the cap rather than disabled: a button
              that does nothing when pressed reads as a bug. */}
          {isDate && (box.dateCount ?? 1) < MAX_DATE_PLACEMENTS_PER_SIGNATURE && (
            <Tooltip title={t('signaturePlacement.duplicateDate')}>
              <button
                type="button"
                aria-label={t('signaturePlacement.duplicateDate')}
                onClick={onDuplicateDate}
                style={boxControlStyle}
              >
                <CopyOutlined style={{ fontSize: 10 }} />
              </button>
            </Tooltip>
          )}
          {isInput && (box.inputCount ?? 1) < MAX_INPUT_PLACEMENTS_PER_SIGNATURE && (
            <Tooltip title={t('signaturePlacement.duplicateInput')}>
              <button
                type="button"
                aria-label={t('signaturePlacement.duplicateInput')}
                onClick={onDuplicateInput}
                style={boxControlStyle}
              >
                <CopyOutlined style={{ fontSize: 10 }} />
              </button>
            </Tooltip>
          )}
          <Tooltip title={t(removeLabelKey(box.kind))}>
            <button
              type="button"
              aria-label={t(removeLabelKey(box.kind))}
              onClick={onDelete}
              style={boxControlStyle}
            >
              <CloseOutlined style={{ fontSize: 10 }} />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

/**
 * Which box the delete X removes — the signature (and with it every companion) or
 * just the one companion clicked.
 */
function removeLabelKey(kind: PlacedBox['kind']): string {
  if (kind === 'date') return 'signaturePlacement.removeDate';
  if (kind === 'input') return 'signaturePlacement.removeInput';
  return 'signaturePlacement.removeBox';
}

/**
 * The same blue the field-properties panel and section card tint their
 * conditions button with, so "this thing is gated" looks identical everywhere
 * the originator meets it.
 */
const CONDITION_COLOR = '#0179C1';

/**
 * Tint for the toggle while the box is collecting initials — the same amber the
 * app uses elsewhere for "this is not the default", so a scan of the page shows
 * which boxes were switched.
 */
const INITIALS_COLOR = '#b45309';

const boxControlStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  padding: 0,
  border: 'none',
  borderRadius: 2,
  background: 'rgba(255,255,255,0.9)',
  color: '#374151',
  cursor: 'pointer',
  lineHeight: 1,
};

/**
 * The add-input button's `>_`, sized to sit level with the 10px icons beside it.
 * Monospace because that is what makes the two characters read as a console
 * prompt rather than as punctuation.
 */
const inputGlyphStyle: CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 9,
  fontWeight: 700,
};
